<script setup lang="ts">
import { NavigationItemType } from "#gql/default";
import type { StoreProductCategory } from "@medusajs/types";
import { toAbsoluteSiteUrl } from "#shared/lib/site-url";

const { data: mainMenu } = useNuxtData<NavigationMenu>("main-menu");
const siteConfig = useSiteConfig();

const { data: product_categories } =
    useNuxtData<StoreProductCategory[]>("categories");
const { data: availableCategories } = useNuxtData<string[]>(
    "available-categories",
);
const emit = defineEmits<{
    (e: "open-element", value: StoreProductCategory): void;
}>();

const categoriesTree = computed(() =>
    getMenuCategories(
        product_categories.value,
        availableCategories.value,
    ),
);

const getNavigationTarget = (item: NavigationItem) =>
    item.path ?? item.externalPath ?? "";

const topBarItems = computed(
    () =>
        mainMenu.value?.filter((item) =>
            Boolean(getNavigationTarget(item)),
        ) ?? [],
);

const navigationSchema = computed(() =>
    topBarItems.value.map((item, index) => ({
        "@type": "SiteNavigationElement" as const,
        "@id": toAbsoluteSiteUrl(
            siteConfig.url,
            `/#main-navigation-${item.id}`,
        ),
        position: index + 1,
        name: item.title,
        url: toAbsoluteSiteUrl(siteConfig.url, getNavigationTarget(item)),
    })),
);

useSchemaOrg(navigationSchema);

const detectPoint = (item: NavigationItem, event: MouseEvent) => {
    const isPlainPrimaryClick =
        event.button === 0 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey;

    if (!isPlainPrimaryClick) return;

    if (item.type === NavigationItemType.WRAPPER && item.path) {
        const pathSegments = item.path
            .split(/[?#]/)[0]
            ?.split("/")
            .filter(Boolean);
        const catalogSegmentIndex = pathSegments?.indexOf("catalog") ?? -1;
        const handle = pathSegments?.[catalogSegmentIndex + 1];

        if (catalogSegmentIndex >= 0 && handle) {
            const category = categoriesTree.value.find(
                (category) => category.handle === handle,
            );

            if (category) {
                event.preventDefault();
                emit("open-element", category);
            }
        }
    }
};
</script>
<template>
    <nav
        v-if="topBarItems.length"
        aria-label="Основные разделы каталога"
        class="hidden lg:block text-inherit"
    >
        <ul class="flex text-inherit flex-wrap gap-6">
            <li v-for="category of topBarItems" :key="category.id">
                <NuxtLink prefetch-on="interaction"
                    :to="getNavigationTarget(category)"
                    :external="category.type === NavigationItemType.EXTERNAL"
                    class="text-base cursor-pointer text-inherit leading-5 uppercase hover:underline underline-offset-3"
                    @click="detectPoint(category, $event)"
                >
                    {{ category.title }}
                </NuxtLink>
            </li>
        </ul>
    </nav>
</template>
